import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import '../tourStyles.scss';
import { getTourState, getOnboardingRole, markTourComplete } from '../tourApi';
import { setActiveDriver } from '../tourManager';

const SESSION_STARTED_KEY = 'solvy_shopper_tour_started';

export function initShopperTour() {
  const state = getTourState();

  // Only run if welcome is complete and shopper tour hasn't run
  if (!state.welcome || state.shopper) return;

  // Only auto-run for users who picked the shopper role.
  // (Freelancers / nonprofits get their own tours.) If the role
  // isn't set — e.g. legacy users from before this was tracked —
  // skip silently rather than ambushing them with the wrong tour.
  if (getOnboardingRole() !== 'shopper') return;

  // Guard against re-firing on every Turbolinks navigation: tourManager
  // destroys the driver on `before-visit` which calls markTourComplete,
  // but that PATCH races the next page request, so server state can
  // still be stale when the new page renders. The session flag wins.
  try {
    if (sessionStorage.getItem(SESSION_STARTED_KEY)) return;
  } catch (e) { /* sessionStorage unavailable; fall through */ }

  // Check we're on a relevant page
  const path = window.location.pathname;
  const isRelevantPage = path === '/' || path === '/products' || path === '/talents';
  if (!isRelevantPage) return;

  // Don't start if the welcome modal is currently open
  if (document.querySelector('.onboarding-overlay')) return;

  // Wait a bit for DOM to settle after Turbolinks load
  setTimeout(() => {
    const steps = [];

    // Target the visible Causes dropdown wrapper (not the hidden Talent/Products ones)
    const causesWrapper = document.querySelector('.menu-item-wrapper:not(.tw-hidden)');
    if (causesWrapper) {
      steps.push({
        element: causesWrapper,
        popover: {
          title: 'Explore Causes',
          description: 'Browse causes that matter to you. Every project is tied to one or more causes.'
        }
      });
    }

    // Target the visible desktop Talent link (not the mobile menu one)
    const talentsLink = document.querySelector('header > div > div > a[href="/talents"]');
    if (talentsLink && talentsLink.offsetParent !== null) {
      steps.push({
        element: talentsLink,
        popover: {
          title: 'Hire Talent',
          description: 'Browse services and classes from people who want to do good. A significant portion of every hire funds a nonprofit project of your choice.'
        }
      });
    }

    // Target the visible desktop Products link
    const productsLink = document.querySelector('header > div > div > a[href="/products"]');
    if (productsLink && productsLink.offsetParent !== null) {
      steps.push({
        element: productsLink,
        popover: {
          title: 'Shop Products',
          description: 'Browse products from sellers who care. A significant portion of every purchase funds a nonprofit project of your choice.'
        }
      });
    }

    const userMenu = document.querySelector('.user-menu');
    if (userMenu) {
      steps.push({
        element: '.user-menu',
        popover: {
          title: 'Your Profile Menu',
          description: 'Access your activity, profile, and settings from here.'
        }
      });
    }

    if (steps.length === 0) return;

    const driverInstance = driver({
      showProgress: true,
      steps: steps,
      nextBtnText: 'Next',
      prevBtnText: 'Back',
      doneBtnText: 'Got it!',
      onDestroyed: () => {
        markTourComplete('shopper');
      }
    });

    setActiveDriver(driverInstance);
    try {
      sessionStorage.setItem(SESSION_STARTED_KEY, '1');
    } catch (e) { /* sessionStorage unavailable; non-fatal */ }
    driverInstance.drive();
  }, 500);
}
